"use client";

import { c as _c } from "react/compiler-runtime";
import { useRef, useState, useCallback, useContext, useMemo } from "react";
import type { ReactNode } from "react";
import type { CreateDropConfig } from "@/entities/IDrop";
import CreateDropStormParts from "./CreateDropStormParts";
import { AnimatePresence, motion } from "framer-motion";
import CreateDropContent from "./CreateDropContent";
import CreateCurationDropContent from "./CreateCurationDropContent";
import QuorumProposalDropModal from "./quorum/QuorumProposalDropModal";
import { useMutation } from "@tanstack/react-query";
import type { ApiWave } from "@/generated/models/ApiWave";
import { ReactQueryWrapperContext } from "../react-query-wrapper/ReactQueryWrapper";
import { commonApiPost } from "@/services/api/common-api";
import type { ApiCreateDropRequest } from "@/generated/models/ApiCreateDropRequest";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { AuthContext } from "../auth/Auth";
import { useKeyPressEvent } from "react-use";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import type { CurationComposerVariant, IdentityPickerPlacement } from "./dropComposer.types";
import { DropMode } from "./dropComposer.types";
import type { DropPrivileges } from "@/hooks/useDropPriviledges";
import { useMyStream } from "@/contexts/wave/MyStreamContext";
import { ProcessIncomingDropType } from "@/contexts/wave/hooks/useWaveRealtimeUpdater";
import { useUnreadDividerOptional } from "@/contexts/wave/UnreadDividerContext";
import { useWave } from "@/hooks/useWave";
import { resolveWaveSubmissionExperience, WaveSubmissionExperience } from "@/helpers/waves/wave-submission-experience.helpers";
import { getMentionedGroupsFromParts } from "@/helpers/waves/drop-group-mentions";
interface CreateDropProps {
  readonly activeDrop: ActiveDropState | null;
  readonly onCancelReplyQuote: () => void;
  readonly onDropAddedToQueue: () => void;
  readonly onAllDropsAdded?: (() => void) | undefined;
  readonly onExitFixedDropMode?: (() => void) | undefined;
  readonly wave: ApiWave;
  readonly dropId: string | null;
  readonly fixedDropMode: DropMode;
  readonly privileges: DropPrivileges;
  readonly curationComposerVariant?: CurationComposerVariant | undefined;
  readonly initialCurationUrl?: string | null | undefined;
  readonly onSubmitCurationUrl?: ((url: string) => void) | undefined;
  readonly canSubmitCurationUrl?: boolean | undefined;
  readonly curationUrlSubmitRestrictionMessage?: string | null | undefined;
  readonly externalAttachmentDrop?: {
    readonly token: number;
    readonly files: File[];
  } | null | undefined;
  readonly onExternalAttachmentDropConsumed?: (() => void) | undefined;
  readonly termsSignatureFlowEnabled?: boolean | undefined;
  readonly identityPickerPlacement?: IdentityPickerPlacement | undefined;
}
export interface DropMutationBody {
  readonly drop: ApiCreateDropRequest;
  readonly dropId: string | null;
  readonly onSuccess?: (() => void) | undefined;
  readonly onError?: ((error: unknown) => void) | undefined;
}
const ANIMATION_DURATION = 0.3;
export default function CreateDrop(t0) {
  const $ = _c(122);
  const {
    activeDrop,
    onCancelReplyQuote,
    onDropAddedToQueue,
    onAllDropsAdded,
    onExitFixedDropMode,
    wave,
    dropId,
    fixedDropMode,
    privileges,
    curationComposerVariant: t1,
    initialCurationUrl: t2,
    onSubmitCurationUrl,
    canSubmitCurationUrl,
    curationUrlSubmitRestrictionMessage: t3,
    externalAttachmentDrop,
    onExternalAttachmentDropConsumed,
    termsSignatureFlowEnabled: t4,
    identityPickerPlacement: t5
  } = t0;
  const curationComposerVariant = t1 === undefined ? "default" : t1;
  const initialCurationUrlProp = t2 === undefined ? null : t2;
  const curationUrlSubmitRestrictionMessage = t3 === undefined ? null : t3;
  const termsSignatureFlowEnabled = t4 === undefined ? true : t4;
  const identityPickerPlacement = t5 === undefined ? "modal" : t5;
  const {
    setToast
  } = useContext(AuthContext);
  const {
    waitAndInvalidateDrops
  } = useContext(ReactQueryWrapperContext);
  const unreadDividerContext = useUnreadDividerOptional();
  let t6;
  if ($[0] !== onCancelReplyQuote) {
    t6 = () => onCancelReplyQuote();
    $[0] = onCancelReplyQuote;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  useKeyPressEvent("Escape", t6);
  const [isStormMode, setIsStormMode] = useState(false);
  const [drop, setDrop] = useState(null);
  const [dropModeOverride, setDropModeOverride] = useState(null);
  const [curationPrefillSeed, setCurationPrefillSeed] = useState(null);
  const [dismissedQuorumProposalScope, setDismissedQuorumProposalScope] = useState(null);
  const {
    processDropRemoved,
    processIncomingDrop
  } = useMyStream();
  const {
    isMemesWave,
    isCurationWave,
    isQuorumWave
  } = useWave(wave);
  const t7 = wave.participation.submission_strategy ?? null;
  let t8;
  if ($[2] !== isCurationWave || $[3] !== isMemesWave || $[4] !== isQuorumWave || $[5] !== t7) {
    t8 = resolveWaveSubmissionExperience({
      isMemesWave,
      isCurationWave,
      isQuorumWave,
      submissionStrategy: t7
    });
    $[2] = isCurationWave;
    $[3] = isMemesWave;
    $[4] = isQuorumWave;
    $[5] = t7;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  const submissionExperience = t8;
  let t9;
  if ($[7] !== activeDrop || $[8] !== fixedDropMode || $[9] !== wave.chat.authenticated_user_eligible || $[10] !== wave.participation.authenticated_user_eligible) {
    t9 = () => {
      if (fixedDropMode === DropMode.CHAT) {
        return false;
      }
      if (fixedDropMode === DropMode.PARTICIPATION) {
        return true;
      }
      if (wave.chat.authenticated_user_eligible) {
        return false;
      }
      if (wave.participation.authenticated_user_eligible) {
        return true;
      }
      if (activeDrop) {
        return false;
      }
      return false;
    };
    $[7] = activeDrop;
    $[8] = fixedDropMode;
    $[9] = wave.chat.authenticated_user_eligible;
    $[10] = wave.participation.authenticated_user_eligible;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const getDefaultIsDropMode = t9;
  const activeDropScope = activeDrop === null ? "none" : `${activeDrop.action}:${activeDrop.drop.id}:${activeDrop.partId}`;
  const modeScopeKey = `${wave.id}:${fixedDropMode}:${wave.chat.authenticated_user_eligible}:${wave.participation.authenticated_user_eligible}:${activeDropScope}`;
  const modeScopeToken = modeScopeKey;
  const defaultIsDropMode = getDefaultIsDropMode();
  const isDropMode = dropModeOverride?.scopeKey === modeScopeToken ? dropModeOverride.value : defaultIsDropMode;
  const initialCurationUrl = curationPrefillSeed?.scopeKey === modeScopeToken ? curationPrefillSeed.url : initialCurationUrlProp;
  const isCurationDropMode = submissionExperience === WaveSubmissionExperience.CURATION_LEGACY && isDropMode;
  const isQuorumProposalDropMode = submissionExperience === WaveSubmissionExperience.QUORUM_PROPOSAL && isDropMode;
  const quorumProposalScopeKey = `${modeScopeToken}:${submissionExperience}`;
  const isQuorumProposalModalOpen = isQuorumProposalDropMode && dismissedQuorumProposalScope !== quorumProposalScopeKey;
  const canMentionAll = wave.wave.authenticated_user_eligible_for_admin === true;
  const canUseCurationUrlSubmit = fixedDropMode === DropMode.CHAT ? onSubmitCurationUrl !== undefined && canSubmitCurationUrl !== false : true;
  const curationUrlRestrictionMessage = fixedDropMode === DropMode.CHAT && onSubmitCurationUrl !== undefined && canSubmitCurationUrl === false ? curationUrlSubmitRestrictionMessage : null;
  let t10;
  if ($[12] !== fixedDropMode || $[13] !== setToast || $[14] !== wave.chat.authenticated_user_eligible || $[15] !== wave.participation.authenticated_user_eligible) {
    t10 = newIsDropMode => {
      if (fixedDropMode !== DropMode.BOTH) {
        return false;
      }
      if (newIsDropMode && !wave.participation.authenticated_user_eligible) {
        setToast({
          message: "You are not eligible to drop in this wave",
          type: "error"
        });
        return false;
      }
      if (!newIsDropMode && !wave.chat.authenticated_user_eligible) {
        setToast({
          message: "You are not eligible to chat in this wave",
          type: "error"
        });
        return false;
      }
      return true;
    };
    $[12] = fixedDropMode;
    $[13] = setToast;
    $[14] = wave.chat.authenticated_user_eligible;
    $[15] = wave.participation.authenticated_user_eligible;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const canSwitchDropMode = t10;
  let t11;
  if ($[17] !== canSwitchDropMode || $[18] !== fixedDropMode || $[19] !== modeScopeToken || $[20] !== onExitFixedDropMode) {
    t11 = newIsDropMode_0 => {
      if (!newIsDropMode_0 && fixedDropMode === DropMode.PARTICIPATION && onExitFixedDropMode) {
        setCurationPrefillSeed(null);
        setDismissedQuorumProposalScope(null);
        onExitFixedDropMode();
        return;
      }
      if (!canSwitchDropMode(newIsDropMode_0)) {
        return;
      }
      setCurationPrefillSeed(null);
      setDismissedQuorumProposalScope(null);
      setDropModeOverride({
        scopeKey: modeScopeToken,
        value: newIsDropMode_0
      });
    };
    $[17] = canSwitchDropMode;
    $[18] = fixedDropMode;
    $[19] = modeScopeToken;
    $[20] = onExitFixedDropMode;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const onDropModeChange = t11;
  let t12;
  if ($[22] !== canSubmitCurationUrl || $[23] !== canSwitchDropMode || $[24] !== fixedDropMode || $[25] !== modeScopeToken || $[26] !== onSubmitCurationUrl) {
    t12 = url => {
      if (fixedDropMode === DropMode.CHAT) {
        if (onSubmitCurationUrl && canSubmitCurationUrl !== false) {
          onSubmitCurationUrl(url);
        }
        return;
      }
      if (!canSwitchDropMode(true)) {
        return;
      }
      setCurationPrefillSeed({
        scopeKey: modeScopeToken,
        url
      });
      setDismissedQuorumProposalScope(null);
      setDropModeOverride({
        scopeKey: modeScopeToken,
        value: true
      });
    };
    $[22] = canSubmitCurationUrl;
    $[23] = canSwitchDropMode;
    $[24] = fixedDropMode;
    $[25] = modeScopeToken;
    $[26] = onSubmitCurationUrl;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const onSwitchToDropModeWithUrl = t12;
  let t13;
  if ($[28] !== fixedDropMode || $[29] !== onDropModeChange || $[30] !== onExitFixedDropMode || $[31] !== quorumProposalScopeKey || $[32] !== wave.chat.authenticated_user_eligible) {
    t13 = () => {
      if (fixedDropMode === DropMode.BOTH && wave.chat.authenticated_user_eligible) {
        onDropModeChange(false);
        return;
      }
      if (fixedDropMode === DropMode.PARTICIPATION && onExitFixedDropMode !== undefined) {
        onDropModeChange(false);
        return;
      }
      setDismissedQuorumProposalScope(quorumProposalScopeKey);
    };
    $[28] = fixedDropMode;
    $[29] = onDropModeChange;
    $[30] = onExitFixedDropMode;
    $[31] = quorumProposalScopeKey;
    $[32] = wave.chat.authenticated_user_eligible;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  const onCloseQuorumProposal = t13;
  let t14;
  if ($[34] !== isDropMode || $[35] !== onDropModeChange) {
    t14 = () => {
      setDismissedQuorumProposalScope(null);
      if (!isDropMode) {
        onDropModeChange(true);
      }
    };
    $[34] = isDropMode;
    $[35] = onDropModeChange;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  const onOpenQuorumProposal = t14;
  let t15;
  if ($[37] !== canMentionAll) {
    t15 = partIndex => {
      setDrop(prevDrop => {
        if (!prevDrop) {
          return null;
        }
        const newParts = prevDrop.parts.filter((_, i) => i !== partIndex);
        return {
          ...prevDrop,
          parts: newParts,
          referenced_nfts: prevDrop.referenced_nfts,
          mentioned_users: prevDrop.mentioned_users,
          mentioned_groups: getMentionedGroupsFromParts(newParts, canMentionAll),
          mentioned_waves: prevDrop.mentioned_waves ?? [],
          metadata: prevDrop.metadata
        };
      });
    };
    $[37] = canMentionAll;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  const onRemovePart = t15;
  let t16;
  if ($[39] !== curationComposerVariant || $[40] !== processDropRemoved || $[41] !== processIncomingDrop || $[42] !== setToast || $[43] !== submissionExperience) {
    t16 = (serverDrop, body_0) => {
      if (body_0.dropId) {
        processDropRemoved(body_0.drop.wave_id, body_0.dropId);
      }
      processIncomingDrop(serverDrop, ProcessIncomingDropType.DROP_INSERT);
      body_0.onSuccess?.();
      if (submissionExperience === WaveSubmissionExperience.CURATION_LEGACY && curationComposerVariant === "leaderboard") {
        setToast({
          message: "Drop submitted successfully",
          type: "success"
        });
      }
    };
    $[39] = curationComposerVariant;
    $[40] = processDropRemoved;
    $[41] = processIncomingDrop;
    $[42] = setToast;
    $[43] = submissionExperience;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== processDropRemoved || $[46] !== setToast) {
    t17 = (error, body_1) => {
      setTimeout(() => {
        if (body_1.dropId) {
          processDropRemoved(body_1.drop.wave_id, body_1.dropId);
        }
      }, 0);
      setToast({
        message: error instanceof Error ? error.message : String(error),
        type: "error"
      });
      body_1.onError?.(error);
    };
    $[45] = processDropRemoved;
    $[46] = setToast;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] !== t16 || $[49] !== t17) {
    t18 = {
      mutationFn: _temp,
      onSuccess: t16,
      onError: t17,
      retry: _temp2,
      retryDelay: _temp3
    };
    $[48] = t16;
    $[49] = t17;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  const addDropMutation = useMutation(t18);
  let t19;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = [];
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  const queueRef = useRef(t19);
  const isProcessingRef = useRef(false);
  const hasBatchErrorsRef = useRef(false);
  const inFlightProcessNextDropRef = useRef(null);
  let t20;
  if ($[52] !== addDropMutation || $[53] !== onAllDropsAdded || $[54] !== waitAndInvalidateDrops) {
    t20 = async () => {
      if (isProcessingRef.current || queueRef.current.length === 0) {
        return;
      }
      isProcessingRef.current = true;
      while (queueRef.current.length > 0) {
        const dropRequest = queueRef.current.shift();
        if (dropRequest === undefined) {
          break;
        }
        ;
        try {
          await addDropMutation.mutateAsync(dropRequest);
        } catch (t21) {
          const error_0 = t21;
          hasBatchErrorsRef.current = true;
          console.error("Error processing drop:", error_0);
        }
      }
      isProcessingRef.current = false;
      const shouldNotifyAllDropsAdded = !hasBatchErrorsRef.current;
      hasBatchErrorsRef.current = false;
      waitAndInvalidateDrops();
      if (shouldNotifyAllDropsAdded) {
        onAllDropsAdded?.();
      }
    };
    $[52] = addDropMutation;
    $[53] = onAllDropsAdded;
    $[54] = waitAndInvalidateDrops;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  const processNextDrop = t20;
  let t21;
  if ($[56] !== onDropAddedToQueue || $[57] !== processNextDrop || $[58] !== unreadDividerContext) {
    t21 = dropRequest_0 => {
      queueRef.current.push(dropRequest_0);
      inFlightProcessNextDropRef.current = processNextDrop();
      if (unreadDividerContext) {
        unreadDividerContext.setUnreadDividerSerialNo(null);
      }
      onDropAddedToQueue();
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    };
    $[56] = onDropAddedToQueue;
    $[57] = processNextDrop;
    $[58] = unreadDividerContext;
    $[59] = t21;
  } else {
    t21 = $[59];
  }
  const submitDrop = t21;
  const hasExitFixedDropMode = onExitFixedDropMode !== undefined;
  const t22 = fixedDropMode === DropMode.PARTICIPATION && hasExitFixedDropMode ? "Close create drop" : null;
  const t23 = fixedDropMode === DropMode.BOTH && privileges.chatRestriction === null || fixedDropMode === DropMode.PARTICIPATION && hasExitFixedDropMode;
  let t24;
  if ($[60] !== activeDrop || $[61] !== canUseCurationUrlSubmit || $[62] !== curationUrlRestrictionMessage || $[63] !== drop || $[64] !== dropId || $[65] !== externalAttachmentDrop || $[66] !== identityPickerPlacement || $[67] !== isDropMode || $[68] !== isStormMode || $[69] !== onCancelReplyQuote || $[70] !== onDropModeChange || $[71] !== onExternalAttachmentDropConsumed || $[72] !== onSwitchToDropModeWithUrl || $[73] !== submitDrop || $[74] !== t22 || $[75] !== t23 || $[76] !== termsSignatureFlowEnabled) {
    t24 = {
      activeDrop,
      onCancelReplyQuote,
      drop,
      isStormMode,
      isDropMode,
      dropId,
      setDrop,
      setIsStormMode,
      onDropModeChange,
      onSwitchToDropModeWithUrl,
      submitDrop,
      dropModeToggleExitLabel: t22,
      canExitDropMode: t23,
      externalAttachmentDrop,
      onExternalAttachmentDropConsumed,
      canSubmitCurationUrl: canUseCurationUrlSubmit,
      curationUrlSubmitRestrictionMessage: curationUrlRestrictionMessage,
      termsSignatureFlowEnabled,
      identityPickerPlacement
    };
    $[60] = activeDrop;
    $[61] = canUseCurationUrlSubmit;
    $[62] = curationUrlRestrictionMessage;
    $[63] = drop;
    $[64] = dropId;
    $[65] = externalAttachmentDrop;
    $[66] = identityPickerPlacement;
    $[67] = isDropMode;
    $[68] = isStormMode;
    $[69] = onCancelReplyQuote;
    $[70] = onDropModeChange;
    $[71] = onExternalAttachmentDropConsumed;
    $[72] = onSwitchToDropModeWithUrl;
    $[73] = submitDrop;
    $[74] = t22;
    $[75] = t23;
    $[76] = termsSignatureFlowEnabled;
    $[77] = t24;
  } else {
    t24 = $[77];
  }
  const createDropContentProps = t24;
  let dropComposerContent;
  if (isQuorumProposalDropMode) {
    let t25;
    if ($[78] !== activeDrop || $[79] !== dropId || $[80] !== isQuorumProposalModalOpen || $[81] !== onCancelReplyQuote || $[82] !== onCloseQuorumProposal || $[83] !== submitDrop || $[84] !== termsSignatureFlowEnabled || $[85] !== wave) {
      t25 = <QuorumProposalDropModal isOpen={isQuorumProposalModalOpen} activeDrop={activeDrop} onCancelReplyQuote={onCancelReplyQuote} wave={wave} dropId={dropId} submitDrop={submitDrop} onClose={onCloseQuorumProposal} termsSignatureFlowEnabled={termsSignatureFlowEnabled} />;
      $[78] = activeDrop;
      $[79] = dropId;
      $[80] = isQuorumProposalModalOpen;
      $[81] = onCancelReplyQuote;
      $[82] = onCloseQuorumProposal;
      $[83] = submitDrop;
      $[84] = termsSignatureFlowEnabled;
      $[85] = wave;
      $[86] = t25;
    } else {
      t25 = $[86];
    }
    let t26;
    if ($[87] !== isQuorumProposalModalOpen || $[88] !== onOpenQuorumProposal) {
      t26 = !isQuorumProposalModalOpen && <div className="tw-flex tw-w-full tw-justify-end"><button type="button" onClick={onOpenQuorumProposal} className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-iron-100 tw-transition desktop-hover:hover:tw-border-iron-500 desktop-hover:hover:tw-bg-iron-800">Create Proposal</button></div>;
      $[87] = isQuorumProposalModalOpen;
      $[88] = onOpenQuorumProposal;
      $[89] = t26;
    } else {
      t26 = $[89];
    }
    let t27;
    if ($[90] !== t25 || $[91] !== t26) {
      t27 = <>{t25}{t26}</>;
      $[90] = t25;
      $[91] = t26;
      $[92] = t27;
    } else {
      t27 = $[92];
    }
    dropComposerContent = t27;
  } else {
    if (isCurationDropMode) {
      let t25;
      if ($[93] !== activeDrop || $[94] !== curationComposerVariant || $[95] !== dropId || $[96] !== initialCurationUrl || $[97] !== isDropMode || $[98] !== onCancelReplyQuote || $[99] !== submitDrop || $[100] !== termsSignatureFlowEnabled || $[101] !== wave) {
        t25 = <CreateCurationDropContent activeDrop={activeDrop} onCancelReplyQuote={onCancelReplyQuote} wave={wave} dropId={dropId} isDropMode={isDropMode} initialUrl={initialCurationUrl} submitDrop={submitDrop} curationComposerVariant={curationComposerVariant} termsSignatureFlowEnabled={termsSignatureFlowEnabled} />;
        $[93] = activeDrop;
        $[94] = curationComposerVariant;
        $[95] = dropId;
        $[96] = initialCurationUrl;
        $[97] = isDropMode;
        $[98] = onCancelReplyQuote;
        $[99] = submitDrop;
        $[100] = termsSignatureFlowEnabled;
        $[101] = wave;
        $[102] = t25;
      } else {
        t25 = $[102];
      }
      dropComposerContent = t25;
    } else {
      let t25;
      if ($[103] !== createDropContentProps || $[104] !== submissionExperience || $[105] !== wave) {
        t25 = <CreateDropContent {...createDropContentProps} wave={wave} submissionExperience={submissionExperience} />;
        $[103] = createDropContentProps;
        $[104] = submissionExperience;
        $[105] = wave;
        $[106] = t25;
      } else {
        t25 = $[106];
      }
      dropComposerContent = t25;
    }
  }
  let t25;
  if ($[107] !== drop?.mentioned_groups || $[108] !== drop?.mentioned_users || $[109] !== drop?.mentioned_waves || $[110] !== drop?.parts || $[111] !== drop?.referenced_nfts || $[112] !== isCurationDropMode || $[113] !== isQuorumProposalDropMode || $[114] !== isStormMode || $[115] !== onRemovePart) {
    t25 = isStormMode && !isCurationDropMode && !isQuorumProposalDropMode && <motion.div initial={{
      opacity: 0,
      height: 0
    }} animate={{
      opacity: 1,
      height: "auto"
    }} exit={{
      opacity: 0,
      height: 0
    }} transition={{
      duration: ANIMATION_DURATION
    }}><CreateDropStormParts parts={drop?.parts ?? []} mentionedUsers={drop?.mentioned_users ?? []} mentionedGroups={drop?.mentioned_groups ?? []} mentionedWaves={drop?.mentioned_waves ?? []} referencedNfts={drop?.referenced_nfts ?? []} onRemovePart={onRemovePart} /></motion.div>;
    $[107] = drop?.mentioned_groups;
    $[108] = drop?.mentioned_users;
    $[109] = drop?.mentioned_waves;
    $[110] = drop?.parts;
    $[111] = drop?.referenced_nfts;
    $[112] = isCurationDropMode;
    $[113] = isQuorumProposalDropMode;
    $[114] = isStormMode;
    $[115] = onRemovePart;
    $[116] = t25;
  } else {
    t25 = $[116];
  }
  let t26;
  if ($[117] !== t25) {
    t26 = <AnimatePresence>{t25}</AnimatePresence>;
    $[117] = t25;
    $[118] = t26;
  } else {
    t26 = $[118];
  }
  let t27;
  if ($[119] !== dropComposerContent || $[120] !== t26) {
    t27 = <>{t26}{dropComposerContent}</>;
    $[119] = dropComposerContent;
    $[120] = t26;
    $[121] = t27;
  } else {
    t27 = $[121];
  }
  return t27;
}
function _temp3(failureCount_0) {
  return failureCount_0 * 1000;
}
function _temp2(failureCount) {
  return failureCount < 3;
}
async function _temp(body) {
  return commonApiPost({
    endpoint: "drops",
    body: body.drop
  });
}